import React, { useEffect, useState } from 'react';
import { Server, Plus, Loader2, Check, Copy, KeyRound, AlertTriangle, ExternalLink, ShieldCheck, Power, Sparkles, ChevronDown, ChevronUp, RefreshCw, CalendarClock, Database, Search } from 'lucide-react';
import {
  Silo, SiloSurvey, SiloSession, SiloAnalysis, NationalInsight,
  listMySilos, registerSilo, setSiloStatus, getSiloSession, listSiloSurveysAll, analyzeSiloSurvey, siloText,
  renewSilo, isSiloActive, queryNationalData,
} from '../services/siloService';

// Researcher screen to register and manage BYOI survey silos. A silo is a survey
// server the researcher runs on their OWN Supabase (see the Tinaney-Silo repo);
// responses collected through it live there, never on Tinaney.
const SiloSetupView: React.FC = () => {
  const [silos, setSilos] = useState<Silo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ silo_id: string; publish_secret: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [openAnalyze, setOpenAnalyze] = useState<string | null>(null);
  const [renewing, setRenewing] = useState<string | null>(null);

  const [form, setForm] = useState({ name: '', silo_url: '', silo_anon_key: '', authenticate_url: '', logo_url: '' });

  const load = async () => {
    setLoading(true);
    try { setSilos(await listMySilos()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = (label: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1800);
  };

  const submit = async () => {
    setError(null);
    if (!form.name || !form.silo_url || !form.silo_anon_key || !form.authenticate_url) {
      setError('Name, silo URL, anon key and authenticate URL are all required.');
      return;
    }
    setSaving(true);
    try {
      const res = await registerSilo(form);
      setSecret(res);
      setShowForm(false);
      setForm({ name: '', silo_url: '', silo_anon_key: '', authenticate_url: '', logo_url: '' });
      await load();
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Could not register the silo.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (s: Silo) => {
    try { await setSiloStatus(s.id, s.status === 'active' ? 'disabled' : 'active'); await load(); }
    catch (e) { console.error(e); }
  };

  const renew = async (s: Silo) => {
    setRenewing(s.id);
    try { await renewSilo(s.id); await load(); }
    catch (e: any) { alert(e?.message || 'Could not renew the silo.'); }
    finally { setRenewing(null); }
  };

  const field = (label: string, key: keyof typeof form, placeholder: string, mono = false) => (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className={`w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="border-b border-slate-700 pb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white mb-1 flex items-center gap-3">
            <Server className="text-indigo-500" /> My Silos
          </h1>
          <p className="text-slate-400 text-sm">Run surveys on your own Supabase. Responses stay on your server — Tinaney only helps people discover and take them.</p>
        </div>
        <button onClick={() => { setShowForm((v) => !v); setSecret(null); }}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium shrink-0">
          <Plus size={16} /> Register silo
        </button>
      </div>

      {/* One-time publish secret reveal */}
      {secret && (
        <div className="bg-emerald-900/20 border border-emerald-800/50 rounded-xl p-5">
          <h3 className="text-emerald-300 font-bold flex items-center gap-2 mb-2"><KeyRound size={18} /> Silo registered — save your publish secret</h3>
          <p className="text-slate-300 text-sm mb-3">Shown once. Set it as <code className="text-emerald-300">SILO_PUBLISH_SECRET</code> in your silo's <code>sync-to-hub</code> function secrets.</p>
          <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
            <code className="text-emerald-300 text-sm font-mono break-all flex-1">{secret.publish_secret}</code>
            <button onClick={() => copy('secret', secret.publish_secret)} className="text-slate-400 hover:text-white shrink-0">
              {copied === 'secret' ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
            </button>
          </div>
          <p className="text-slate-500 text-xs mt-2">Silo id: <span className="font-mono">{secret.silo_id}</span></p>
        </div>
      )}

      {/* Register form */}
      {showForm && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Silo name', 'name', 'My Research Silo')}
            {field('Logo URL (optional)', 'logo_url', 'https://…/logo.png')}
            {field('Silo Supabase URL', 'silo_url', 'https://xxxx.supabase.co', true)}
            {field('Silo anon key', 'silo_anon_key', 'sb_publishable_…', true)}
          </div>
          {field('authenticate-hub-user function URL', 'authenticate_url', 'https://xxxx.supabase.co/functions/v1/authenticate-hub-user', true)}
          <p className="text-xs text-slate-500">Creating a silo starts a <span className="text-amber-300 font-medium">$50/month subscription</span> — 5000 credits are charged now for the first month. Rotating the secret later (re-registering the same URL) is free.</p>
          {error && <p className="text-red-400 text-xs flex items-center gap-1.5"><AlertTriangle size={14} /> {error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-700">Cancel</button>
            <button onClick={submit} disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Register
            </button>
          </div>
        </div>
      )}

      {/* Deploy checklist */}
      <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-5">
        <h3 className="text-white font-bold flex items-center gap-2 mb-3"><ShieldCheck size={18} className="text-indigo-400" /> Standing up a silo</h3>
        <ol className="text-sm text-slate-400 space-y-1.5 list-decimal list-inside">
          <li>Clone the <span className="text-slate-200 font-medium">Tinaney-Silo</span> template into a new Supabase project.</li>
          <li>Deploy <code className="text-indigo-300">authenticate-hub-user</code> (Verify JWT <b>OFF</b>) and run <code>supabase/schema.sql</code>.</li>
          <li>Set its secrets: <code>HUB_URL</code>, <code>HUB_ANON_KEY</code>, <code>SILO_JWT_SECRET</code>.</li>
          <li>Register the silo here to get your <code className="text-emerald-300">publish_secret</code>, then deploy <code>sync-to-hub</code>.</li>
          <li>Optional: deploy <code className="text-indigo-300">analyze-survey</code> and set <code>GEMINI_API_KEY</code> to analyse results with your own Gemini key.</li>
        </ol>
        <a href="https://github.com/sengtha/Tinaney-Silo" target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 text-sm mt-3">
          Tinaney-Silo template <ExternalLink size={13} />
        </a>
      </div>

      {/* My silos */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>
      ) : silos.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-8">No silos yet. Register one above to collect survey responses on your own server.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {silos.map((s) => (
            <div key={s.id} className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {s.logo_url
                    ? <img src={s.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-700" />
                    : <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center"><Server size={18} className="text-indigo-400" /></div>}
                  <div className="min-w-0">
                    <h4 className="text-white font-semibold truncate">{s.name}</h4>
                    <p className="text-slate-500 text-xs font-mono truncate">{s.silo_url}</p>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border shrink-0 ${
                  isSiloActive(s) ? 'text-emerald-400 border-emerald-900/40 bg-emerald-900/20' : 'text-amber-400 border-amber-900/40 bg-amber-900/20'}`}>
                  {isSiloActive(s) ? 'active' : (s.paid_through ? 'lapsed' : 'unpaid')}
                </span>
              </div>

              <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5">
                <CalendarClock size={12} className="shrink-0" />
                {s.paid_through
                  ? (isSiloActive(s)
                      ? <span>Subscription active until <span className="text-slate-300">{new Date(s.paid_through).toLocaleDateString()}</span></span>
                      : <span>Lapsed {new Date(s.paid_through).toLocaleDateString()} — renew to reappear in discovery</span>)
                  : <span>No active subscription — renew to go live</span>}
              </p>

              <div className="flex items-center justify-between mt-3 gap-2">
                <button onClick={() => setOpenAnalyze(openAnalyze === s.id ? null : s.id)}
                  className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded hover:bg-slate-700">
                  <Sparkles size={13} /> Analyze {openAnalyze === s.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => renew(s)} disabled={renewing === s.id}
                    className="flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 px-2 py-1 rounded hover:bg-slate-700 disabled:opacity-50">
                    {renewing === s.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={13} />} Renew $50
                  </button>
                  <button onClick={() => toggle(s)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700">
                    <Power size={13} /> {s.status === 'active' ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
              {openAnalyze === s.id && <SiloAnalysisPanel silo={s} />}
            </div>
          ))}
        </div>
      )}

      {/* Cambodia national data — paid per query in user credits (silo-tier benefit) */}
      <NationalDataPanel enabled={silos.some(isSiloActive)} />
    </div>
  );
};

// Owner-only panel: connect to the silo, list its surveys, and generate AI
// insights per survey using the silo's OWN Gemini key (analyze-survey function).
const SiloAnalysisPanel: React.FC<{ silo: Silo }> = ({ silo }) => {
  const [session, setSession] = useState<SiloSession | null>(null);
  const [surveys, setSurveys] = useState<SiloSurvey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, SiloAnalysis>>({});

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const sess = await getSiloSession(silo);
        setSession(sess);
        setSurveys(await listSiloSurveysAll(sess));
      } catch (e: any) {
        setError(e?.message || 'Could not connect to this silo.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo.id]);

  const run = async (surveyId: string) => {
    if (!session) return;
    setAnalyzing(surveyId); setError(null);
    try {
      const r = await analyzeSiloSurvey(session, surveyId);
      setResult((m) => ({ ...m, [surveyId]: r }));
    } catch (e: any) {
      setError(e?.message || 'Analysis failed.');
    } finally {
      setAnalyzing(null);
    }
  };

  if (loading) return <div className="mt-4 flex justify-center py-4"><Loader2 className="animate-spin text-indigo-500" size={20} /></div>;

  return (
    <div className="mt-4 border-t border-slate-700 pt-4 space-y-3">
      {error && <p className="text-red-400 text-xs flex items-center gap-1.5"><AlertTriangle size={13} /> {error}</p>}
      {surveys.length === 0 ? (
        <p className="text-slate-500 text-xs">No surveys on this silo yet.</p>
      ) : surveys.map((sv) => {
        const r = result[sv.id];
        return (
          <div key={sv.id} className="bg-slate-900 rounded-lg border border-slate-700 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-white font-medium truncate">{siloText(sv.title, 'Untitled survey')}</p>
              <button onClick={() => run(sv.id)} disabled={analyzing === sv.id}
                className="flex items-center gap-1.5 text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50 shrink-0">
                {analyzing === sv.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {r ? 'Re-run' : 'Insights'}
              </button>
            </div>
            {r && (r.insights ? (
              <div className="mt-3 space-y-2 text-xs">
                <p className="text-slate-300"><span className="text-[10px] uppercase tracking-wider text-indigo-400">Summary</span><br />{r.insights.summary}</p>
                {r.insights.key_findings?.length > 0 && <InsightList title="Key findings" items={r.insights.key_findings} />}
                {r.insights.notable_patterns?.length > 0 && <InsightList title="Patterns" items={r.insights.notable_patterns} />}
                {r.insights.recommendations?.length > 0 && <InsightList title="Recommendations" items={r.insights.recommendations} />}
                <p className="text-[10px] text-slate-600">Based on {r.response_count} completed response(s), computed on your silo with your Gemini key.</p>
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">{r.message || 'No completed responses yet.'}</p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const InsightList: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">{title}</p>
    <ul className="list-disc list-inside space-y-0.5 text-slate-300">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  </div>
);

// Cambodia national-data oracle. A silo-tier benefit: each query hits the Hub's
// public-insight-engine (grounded over the national ledger) and costs user credits.
const NationalDataPanel: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<NationalInsight | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true); setErr(null); setRes(null);
    try { setRes(await queryNationalData(q.trim())); }
    catch (e: any) { setErr(e?.message || 'Query failed.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
      <h3 className="text-white font-bold flex items-center gap-2 mb-1"><Database size={18} className="text-indigo-400" /> Cambodia National Data</h3>
      <p className="text-[11px] text-slate-500 mb-3">Query the national statistical ledger to contextualise your silo's findings. Each query costs <span className="text-amber-300">50 credits ($0.50)</span> from your balance (free monthly quota first).</p>
      {!enabled ? (
        <p className="text-xs text-slate-500 italic">Available while you run an active silo. Register or renew a silo to unlock national-data queries.</p>
      ) : (
        <>
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()}
              placeholder="e.g. rice yield trends in Battambang"
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
            <button onClick={run} disabled={loading || !q.trim()}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 shrink-0">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Ask
            </button>
          </div>
          {err && <p className="text-red-400 text-xs mt-2 flex items-center gap-1.5"><AlertTriangle size={13} /> {err}</p>}
          {res && (
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">Summary</p>
                <p className="text-slate-300 whitespace-pre-wrap">{res.summary}</p>
              </div>
              {res.strategic_recommendation && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">Recommendation</p>
                  <p className="text-slate-300 whitespace-pre-wrap">{res.strategic_recommendation}</p>
                </div>
              )}
              {res.references && res.references.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">Sources</p>
                  <ul className="space-y-1">
                    {res.references.map((r, i) => (
                      <li key={i} className="text-xs text-slate-400"><span className="text-slate-300 font-medium">{r.source_name}</span>{r.context ? ` — ${r.context}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SiloSetupView;
